import { Button } from "@/components/ui/button";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  User,
  LogOut,
  Menu,
  CalendarDays,
  HelpCircle,
  UserCog,
  Search,
  Gauge,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { useState, useRef, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getUserInitials } from "@/utils/userUtils";
import { Logo } from "@/components/ui/Logo";
import { supabase } from "@/lib/supabaseClient";
import { NotificationsPopover } from "@/components/notifications/NotificationsPopover";
import { TaskDropdown } from "@/components/tasks/TaskDropdown";
import { NotificationService } from "@/services/notificationService";
import { clearRequestCache } from "@/lib/supabaseUtils";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { avatarEvents } from "@/utils/avatarEvents";

import { usePermissions } from "@/contexts/PermissionContext";
import { useTheme } from "@/contexts/ThemeContext";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { effectiveRole } from "@/lib/effectiveRole";
import { AccountHealthButton } from "@/components/layout/AccountHealthButton";

export function Header({ toggleSidebar }: { toggleSidebar?: () => void }) {
  const { user, logout, impersonateUser } = useAuth();
  const { currentTenant } = useTenant();
  const { hasPermission } = usePermissions();
  const { isDarkMode } = useTheme();
  const hasSuperadminAccess = hasPermission('system.override') || hasPermission('tenant.manage');
  const location = useLocation();
  const params = useParams();

  // Extract current project ID from URL if on a project page
  const getCurrentProjectId = (): string | undefined => {
    if (location.pathname.includes('/projects/') && params.projectId) {
      return params.projectId;
    }
    return undefined;
  };
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [profile, setProfile] = useState<any>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const subscriptionRef = useRef<any>(null);
  const [avatarLoading, setAvatarLoading] = useState(false);

  // View as functionality
  const [loginAsOpen, setLoginAsOpen] = useState(false);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [userSearch, setUserSearch] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const { toast } = useToast();

  const handleSignOut = async () => {
    try {
      await logout();
      setDropdownOpen(false);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
        setLoginAsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Get user initials for avatar
  const getAvatarInitials = () => {
    // Try profile data first, then fall back to user metadata, then email
    const userObj = {
      id: user?.id || '',
      first_name: profile?.first_name || user?.user_metadata?.first_name,
      last_name: profile?.last_name || user?.user_metadata?.last_name,
      email: profile?.email || user?.email
    };
    return getUserInitials(userObj);
  };

  useEffect(() => {
    async function fetchProfile() {
      if (user && user.id) {
        setLoadingProfile(true);
        const { data, error } = await supabase
          .from("users")
          .select("*, roles(name)")
          .eq("id", user.id)
          .single();
        if (!error && data) {
          setProfile(data);
        }
        setLoadingProfile(false);
      }
    }
    fetchProfile();
  }, [user?.id]); // Only depend on user.id

  // Listen for avatar updates
  useEffect(() => {
    const handleAvatarUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{ avatarUrl: string }>;
      const newAvatarUrl = customEvent.detail.avatarUrl;
      
      // Show loading state briefly
      setAvatarLoading(true);
      
      // Update profile with new avatar
      setProfile((prevProfile: any) => ({
        ...prevProfile,
        avatar_url: newAvatarUrl
      }));

      // Hide loading after a brief delay to show the transition
      setTimeout(() => {
        setAvatarLoading(false);
      }, 500);
    };

    // Add event listener
    avatarEvents.addEventListener('avatar-updated', handleAvatarUpdate);

    // Cleanup
    return () => {
      avatarEvents.removeEventListener('avatar-updated', handleAvatarUpdate);
    };
  }, []);

  const fetchNotifications = async () => {
    if (!user?.id || !currentTenant?.tenant_id) return;
    try {
      const data = await NotificationService.getUserNotifications(user.id, 50);
      setNotifications(data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const handleMarkAllRead = async () => {
    if (!user?.id) return;
    try {
      await NotificationService.markAllAsRead(user.id);

      // Clear the cache to ensure fresh data
      if (currentTenant?.tenant_id) {
        clearRequestCache(`notifications-user-${user.id}-${currentTenant.tenant_id}`);
      }

      await fetchNotifications();
    } catch (error) {
      console.error("[Header] Error marking all notifications as read:", error);
      // Still try to refresh notifications in case some were marked as read
      try {
        // Clear cache even on error
        if (currentTenant?.tenant_id) {
          clearRequestCache(`notifications-user-${user.id}-${currentTenant.tenant_id}`);
        }
        await fetchNotifications();
      } catch (refreshError) {
        console.error("[Header] Error refreshing notifications after failed mark all as read:", refreshError);
      }
    }
  };

  const handleNotificationClick = async (id: string) => {
    if (!user?.id) return;
    try {
      await NotificationService.markAsRead(id, user.id);

      // Clear the cache to ensure fresh data
      if (currentTenant?.tenant_id) {
        clearRequestCache(`notifications-user-${user.id}-${currentTenant.tenant_id}`);
      }

      await fetchNotifications();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  useEffect(() => {
    if (user?.id && currentTenant?.tenant_id) {
      fetchNotifications();

      // Clean up existing subscription
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }

      // Create new subscription
      subscriptionRef.current = NotificationService.subscribeToNotifications(
        user.id,
        (payload) => {
          if (payload.eventType === 'INSERT' && payload.new) {
            // Add the new notification to the top of the list immediately
            setNotifications(prev => [payload.new, ...prev]);
          } else {
            // For other events (UPDATE, DELETE), fall back to refetch
            fetchNotifications();
          }
        },
        currentTenant.tenant_id
      );

      return () => {
        if (subscriptionRef.current) {
          supabase.removeChannel(subscriptionRef.current);
          subscriptionRef.current = null;
        }
      };
    } else {
      // Clear notifications when user or tenant is not available
      setNotifications([]);
    }
  }, [user?.id, currentTenant?.tenant_id]); // Depend on both user and tenant

  // Fetch all users for admin login-as functionality
  /**
   * ⚠️ THIS LIST SHOWED THE LEGACY ROLE, WHICH IS D-2 IN A SURFACE THAT FIX DID NOT
   * REACH. It selected `role_id, roles(name)` and rendered `roles.name`, and that
   * embed resolves the legacy `users.role_id` column, always. Measured against the
   * one sub-account-enabled account:
   *
   *   carlos+trippertenant@   menu said "Admin"          holds Agency Admin here
   *
   * D-2 fixed the same expression in `components/settings/components/users/
   * UsersTab.tsx` and this menu was not part of that change. The rule is the one
   * SUB-2.6 wrote and `effectiveRole.ts` encodes: the role shown for the account
   * being viewed is the role held IN that account, and the legacy column is the
   * fallback for people who hold none there.
   *
   * ⚠️ THIS LIST IS PLATFORM-WIDE AND THE RULE IS PER-TENANT, so most rows will fall
   * back to the legacy column and that is correct rather than a compromise: a user
   * with no assignment in the account being viewed has no per-tenant role here to
   * show. What changes is that a user who DOES hold one stops being mislabelled.
   *
   * ⚠️ `list_tenant_role_assignments` RATHER THAN A DIRECT `user_roles` READ, and
   * that is not a style choice. `user_roles` SELECT is
   * `check_user_role('Admin') OR auth.uid() = user_id`, so a caller reading it for
   * OTHER users gets rows only if they hold Admin in the currently resolved tenant.
   * That is the defect D-2 hit: the column could not read the roles it displays.
   * The RPC (20260813e) is SECURITY DEFINER and carries its own gate.
   *
   * A failed role fetch leaves the legacy fallback in place rather than blanking the
   * labels. Unknown is not "no role", which is the existing rule on the sibling
   * branch in TenantContext.
   */
  const fetchAllUsers = async () => {
    if (!hasSuperadminAccess) return;

    setLoadingUsers(true);
    try {
      const { data, error } = await supabase
        .from("users")
        .select("id, first_name, last_name, email, role_id, roles(name)")
        .order("first_name");

      if (error) throw error;

      const rows = data || [];
      const tenantId = currentTenant?.tenant_id;
      let heldHere = new Map<string, string[]>();

      if (tenantId) {
        const { data: assignments, error: roleError } = await supabase.rpc(
          "list_tenant_role_assignments",
          { p_tenant_id: tenantId },
        );
        if (roleError) {
          // Not fatal. The labels fall back to the legacy column, which is what
          // they showed before this change.
          console.error(
            "[Header] list_tenant_role_assignments failed, impersonation labels fall back to the legacy role:",
            roleError,
          );
        } else {
          heldHere = (assignments || []).reduce(
            (acc: Map<string, string[]>, row: { user_id: string; role_name: string }) => {
              const names = acc.get(row.user_id) ?? [];
              names.push(row.role_name);
              acc.set(row.user_id, names);
              return acc;
            },
            new Map<string, string[]>(),
          );
        }
      }

      setAllUsers(
        rows.map((u: any) => ({
          ...u,
          // Precomputed here rather than in the render, so the search filter below
          // and the label cannot diverge. They previously shared one wrong
          // expression, so at least they agreed; splitting them would let someone
          // search "Agency Admin", get nothing, and read the words on screen at the
          // same time.
          effective_role: effectiveRole({
            rolesInThisTenant: heldHere.get(u.id),
            legacyRoleName: u.roles?.name ?? null,
          }),
        })),
      );
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleLoginAsUser = async (selectedUser: any) => {
    try {
      const userToImpersonate = {
        id: selectedUser.id,
        email: selectedUser.email,
        user_metadata: {
          first_name: selectedUser.first_name,
          last_name: selectedUser.last_name,
        },
        app_metadata: {
          role_id: selectedUser.role_id,
        },
      } as any;

      await impersonateUser(userToImpersonate);
      toast({
        title: "Impersonating User",
        description: `You are now logged in as ${selectedUser.first_name} ${selectedUser.last_name}`,
      });
      setLoginAsOpen(false);
      setDropdownOpen(false);
    } catch (error) {
      console.error("Error impersonating user:", error);
      toast({
        title: "Error",
        description: "Failed to impersonate user",
        variant: "destructive",
      });
    }
  };

  // Filter users based on search.
  //
  // ⚠️ THE ROLE TERM SEARCHES THE SAME STRING THE LABEL RENDERS. It reads
  // `effective_role.label`, not the legacy `roles.name`, for D-2's reason: the cell
  // and the filter must not disagree, or someone searches "Agency Admin", gets
  // nothing, and reads those exact words on the row beside the empty result. The
  // role term is new here; this filter was name and email only, so nothing that
  // used to match has stopped matching.
  const filteredUsers = allUsers.filter(
    (u) =>
      u.id !== user?.id && // Don't show current user
      (userSearch === "" ||
        `${u.first_name} ${u.last_name}`
          .toLowerCase()
          .includes(userSearch.toLowerCase()) ||
        u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
        (u.effective_role?.label ?? "")
          .toLowerCase()
          .includes(userSearch.toLowerCase()))
  );

  return (
    <header className="fixed top-0 z-[200] w-full border-b bg-background">
      <div className="w-full px-4 flex h-14 items-center">
        <div className="mr-4 flex">
          <Button
            variant="ghost"
            size="icon"
            className="mr-2 md:hidden"
            onClick={toggleSidebar}
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">Toggle sidebar</span>
          </Button>

          {/* Logo */}
          <Link to="/app/dashboard" className="flex items-center">
            <Logo className="h-8" />
          </Link>
        </div>

        <div className="flex flex-1 items-center justify-between space-x-2 md:justify-end">
          <div className="flex items-center space-x-2">
            {/* Dev Roadmap — superadmin only, styled like a code block */}
            {hasPermission("system.override") && (
              <Button
                asChild
                variant="ghost"
                size="icon"
                className={cn(
                  location.pathname.startsWith("/app/roadmap")
                    ? "bg-primary/20 text-primary"
                    : "relative"
                )}
                title="Dev Roadmap"
              >
                <Link to="/app/roadmap">
                  <span className="font-mono text-sm font-bold tracking-tighter">
                    {"</>"}
                  </span>
                  <span className="sr-only">Dev Roadmap</span>
                </Link>
              </Button>
            )}

            {/* SD-9.1 design cost & governance — superadmin only, same gate as the roadmap */}
            {hasPermission("system.override") && (
              <Button
                asChild
                variant="ghost"
                size="icon"
                className={cn(
                  location.pathname === "/app/design-cost"
                    ? "bg-primary/20 text-primary"
                    : "relative"
                )}
                title="Design cost & governance"
              >
                <Link to="/app/design-cost">
                  <Gauge className="h-5 w-5" />
                  <span className="sr-only">Design cost &amp; governance</span>
                </Link>
              </Button>
            )}

            {/* RBAC-5.3 account health — superadmin only, same gate as the roadmap */}
            {hasPermission("system.override") && <AccountHealthButton />}

            {/* Support Icon */}
            <Button
              asChild
              variant="ghost"
              size="icon"
              className={cn(
                location.pathname === "/app/support-tickets" ||
                  location.pathname.startsWith("/app/support-tickets/")
                  ? "bg-primary/20 text-primary"
                  : "relative"
              )}
              title="Support"
            >
              <Link to="/app/support-tickets">
                <HelpCircle className="h-5 w-5" />
              </Link>
            </Button>

            {/* Calendar Icon - Only show for users with calendar permissions */}
            {user &&
              (hasPermission("manage_settings") ||
                hasPermission("system.override")) && (
                <Button
                  asChild
                  variant="ghost"
                  size="icon"
                  className={cn(
                    location.pathname === "/app/scheduler" ||
                      location.pathname.startsWith("/app/scheduler/")
                      ? "bg-primary/20 text-primary"
                      : "relative"
                  )}
                  title="Calendar"
                >
                  <Link to="/app/scheduler">
                    <CalendarDays className="h-5 w-5" />
                  </Link>
                </Button>
              )}

            {/* Tasks Dropdown - Only show for users with project management permissions */}
            {(hasPermission("manage_projects") ||
              hasPermission("edit_projects") ||
              hasPermission("view_projects")) && <TaskDropdown currentProjectId={getCurrentProjectId()} />}

            {/* Notifications Popover */}
            <NotificationsPopover
              notifications={notifications}
              onMarkAllRead={handleMarkAllRead}
              onNotificationClick={handleNotificationClick}
              open={notificationsOpen}
              onOpenChange={setNotificationsOpen}
              onBellClick={() => setNotificationsOpen(!notificationsOpen)}
            />

            {/* User Menu */}
            {user ? (
              <div className="relative ml-2" ref={dropdownRef}>
                <button
                  className="relative flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  <Avatar className={cn(
                    "h-8 w-8 bg-primary/10 text-primary border-2 border-primary transition-all duration-300",
                    avatarLoading && "opacity-60 scale-95"
                  )}>
                    <AvatarFallback>{getAvatarInitials()}</AvatarFallback>
                    <AvatarImage src={profile?.avatar_url || user.user_metadata?.avatar_url} />
                  </Avatar>
                  {avatarLoading && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-popover text-popover-foreground rounded-md shadow-lg py-1 z-[500] border border-border">
                    <div className="px-4 py-2 border-b border-border">
                      <p className="text-sm font-medium text-foreground">
                        {user.email}
                      </p>
                      <div className="px-4 py-1 text-xs text-foreground/60">
                        {loadingProfile
                          ? "Loading..."
                          : profile?.roles?.name || "Unknown Role"}
                      </div>
                    </div>
                    <Link
                      to="/app/profile"
                      className="block px-4 py-2 text-sm text-foreground hover:bg-accent flex items-center transition-colors duration-200"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <User className="h-4 w-4 mr-2 text-foreground/60" />
                      Your Profile
                    </Link>

                    {/* Dark Mode Toggle in Menu */}

                    {/* Superadmin-only Login As option */}
                    {hasSuperadminAccess && (
                      <div className="relative">
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-accent flex items-center"
                          onClick={() => {
                            setLoginAsOpen(!loginAsOpen);
                            if (!loginAsOpen) {
                              fetchAllUsers();
                              setUserSearch(""); // Reset search when opening
                            }
                          }}
                        >
                          <UserCog className="h-4 w-4 mr-2 text-muted-foreground" />
                          View as...
                        </button>

                        {loginAsOpen && (
                          <div className="absolute right-0 top-0 w-80 bg-popover rounded-md shadow-lg border border-border z-[550]">
                            <div className="p-3 border-b border-border">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                  placeholder="Search users..."
                                  value={userSearch}
                                  onChange={(e) =>
                                    setUserSearch(e.target.value)
                                  }
                                  className="pl-10 h-8 text-sm"
                                />
                              </div>
                            </div>
                            <div className="max-h-60 overflow-y-auto">
                              {loadingUsers ? (
                                <div className="p-3 text-center text-sm text-gray-500">
                                  Loading users...
                                </div>
                              ) : filteredUsers.length > 0 ? (
                                filteredUsers.map((selectedUser) => (
                                  <button
                                    key={selectedUser.id}
                                    className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-accent flex flex-col transition-colors duration-200"
                                    onClick={() =>
                                      handleLoginAsUser(selectedUser)
                                    }
                                  >
                                    <span className="font-medium">
                                      {selectedUser.first_name}{" "}
                                      {selectedUser.last_name}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      {selectedUser.email} •{" "}
                                      {/*
                                        The role held in the account being viewed,
                                        falling back to the legacy column. Was
                                        `selectedUser.roles?.name`, which is the
                                        legacy column always: see the note on
                                        fetchAllUsers. `label` is already a dash when
                                        there is nothing to show, so "No Role" only
                                        appears if the field itself is missing.
                                      */}
                                      {selectedUser.effective_role?.label ||
                                        selectedUser.roles?.name ||
                                        "No Role"}
                                    </span>
                                  </button>
                                ))
                              ) : (
                                <div className="p-3 text-center text-sm text-muted-foreground">
                                  No users found
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="px-4 py-3 border-t border-border">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-foreground">
                          {isDarkMode ? "Dark Mode" : "Light Mode"}
                        </span>
                        <ThemeToggle size="sm" />
                      </div>
                    </div>

                    <div className="border-t border-border my-1"></div>
                    <button
                      onClick={handleSignOut}
                      className="block w-full text-left px-4 py-2 text-sm text-foreground hover:bg-accent flex items-center transition-colors duration-200"
                    >
                      <LogOut className="h-4 w-4 mr-2 text-foreground/60" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login">
                <Button className="bg-primary hover:bg-primaryHover text-white ml-2">
                  Sign In
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
